import { ANALYTICS_CONFIG, getAgeRange, getDeviceType, getReferrerCategory } from './config';

class SessionManager {
  constructor() {
    this.sessionId = this._getOrCreateSessionId();
    this.sessionStart = Date.now();
    this.currentStep = null;
    this.stepStartTime = null;
    this.visitedSteps = new Set();
    this.completedSteps = [];
    this.stepErrors = {};
    this.validationAttempts = {};
    this.lastFieldFocused = null;
    this.lastFieldHadValue = false;
    this.fieldFocusEmitted = false;
    this.fieldsModified = new Set();
    this.conditionalsShown = [];
    this.userProfile = {};

    // Persister les UTM dès l'arrivée (avant que l'URL ne change avec le routing SPA)
    this._persistUtmParams();
  }

  // Sauvegarde les UTM en sessionStorage pour ne pas les perdre avec le routing SPA
  _persistUtmParams() {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const utmParams = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content'];

      utmParams.forEach(param => {
        const value = urlParams.get(param);
        if (value) {
          sessionStorage.setItem(`sim_${param}`, value);
        }
      });
    } catch (e) {
      // sessionStorage non disponible
    }
  }

  // Génère ou récupère l'ID de session
  _getOrCreateSessionId() {
    const storageKey = 'sim_session_id';
    const expireKey = 'sim_session_expire';

    try {
      const existing = sessionStorage.getItem(storageKey);
      const expireTime = sessionStorage.getItem(expireKey);

      if (existing && expireTime && Date.now() < parseInt(expireTime, 10)) {
        this._refreshExpiry();
        return existing;
      }
    } catch (e) {
      // sessionStorage non disponible
    }

    const newId = this._generateSessionId();
    try {
      sessionStorage.setItem(storageKey, newId);
      this._refreshExpiry();
    } catch (e) {
      // Ignore si sessionStorage non disponible
    }
    return newId;
  }

  _refreshExpiry() {
    try {
      sessionStorage.setItem('sim_session_expire', Date.now() + ANALYTICS_CONFIG.sessionTimeoutMs);
    } catch (e) {
      // Ignore
    }
  }

  _generateSessionId() {
    return 'sess_' + Date.now().toString(36) + '_' + Math.random().toString(36).substr(2, 9);
  }

  // Getters
  getSessionId() {
    this._refreshExpiry();
    return this.sessionId;
  }

  getCurrentStep() {
    return this.currentStep;
  }

  getTimeOnCurrentStep() {
    if (!this.stepStartTime) return 0;
    return Date.now() - this.stepStartTime;
  }

  getSessionDuration() {
    return Date.now() - this.sessionStart;
  }

  hasVisitedStep(stepNumber) {
    return this.visitedSteps.has(stepNumber);
  }

  getCompletedSteps() {
    return [...this.completedSteps];
  }

  getErrorCount(stepNumber) {
    return (this.stepErrors[stepNumber] || []).length;
  }

  getTotalErrors() {
    return Object.values(this.stepErrors).reduce((sum, errors) => sum + errors.length, 0);
  }

  getValidationAttempts(stepNumber) {
    return this.validationAttempts[stepNumber] || 0;
  }

  getTotalValidationAttempts() {
    return Object.values(this.validationAttempts).reduce((sum, count) => sum + count, 0);
  }

  getFieldsModifiedCount() {
    return this.fieldsModified.size;
  }

  getLastFieldFocused() {
    return this.lastFieldFocused;
  }

  getLastFieldHadValue() {
    return this.lastFieldHadValue;
  }

  // Méthodes de tracking
  startStep(stepNumber) {
    this.currentStep = stepNumber;
    this.stepStartTime = Date.now();
    this.visitedSteps.add(stepNumber);
    this.fieldsModified.clear();
    this.lastFieldFocused = null;
    this._refreshExpiry();
  }

  endStep(stepNumber) {
    const duration = Date.now() - (this.stepStartTime || Date.now());
    if (!this.completedSteps.includes(stepNumber)) {
      this.completedSteps.push(stepNumber);
    }
    this._refreshExpiry();
    return duration;
  }

  recordError(stepNumber, errorCode) {
    if (!this.stepErrors[stepNumber]) {
      this.stepErrors[stepNumber] = [];
    }
    this.stepErrors[stepNumber].push({
      code: errorCode,
      timestamp: Date.now()
    });
  }

  recordValidationAttempt(stepNumber) {
    this.validationAttempts[stepNumber] = (this.validationAttempts[stepNumber] || 0) + 1;
  }

  recordFieldFocus(fieldId) {
    this.lastFieldFocused = fieldId;
  }

  // Un seul event field_focus est émis par session (il ne sert que de booléen
  // "la session a interagi" côté dashboard). Évite ~1/3 du volume d'événements.
  hasEmittedFieldFocus() {
    return this.fieldFocusEmitted;
  }

  markFieldFocusEmitted() {
    this.fieldFocusEmitted = true;
  }

  recordFieldBlur(fieldId, hadValue) {
    if (this.lastFieldFocused === fieldId) {
      this.lastFieldHadValue = hadValue;
    }
  }

  recordFieldModified(fieldId) {
    this.fieldsModified.add(fieldId);
  }

  recordConditionalShown(conditionalId, triggerField) {
    this.conditionalsShown.push({
      id: conditionalId,
      trigger: triggerField,
      timestamp: Date.now(),
      stepNumber: this.currentStep
    });
  }

  getLastConditionalShown() {
    return this.conditionalsShown[this.conditionalsShown.length - 1] || null;
  }

  // Profil utilisateur (pour segmentation)
  updateUserProfile(data) {
    this.userProfile = { ...this.userProfile, ...data };
  }

  setAge(age) {
    this.userProfile.age = age;
    this.userProfile.age_range = getAgeRange(age);
  }

  setHasChildren(hasChildren, childrenCount = 0) {
    this.userProfile.has_children = hasChildren;
    this.userProfile.children_count = childrenCount;
  }

  setIsEmployed(isEmployed) {
    this.userProfile.is_employed = isEmployed;
  }

  setHasPartner(hasPartner) {
    this.userProfile.has_partner = hasPartner;
  }

  setHousingType(housingType) {
    this.userProfile.housing_type = housingType;
  }

  setDepartment(postalCode) {
    if (postalCode && postalCode.length >= 2) {
      this.userProfile.department = postalCode.substring(0, 2);
    }
  }

  setSituation(situations) {
    this.userProfile.is_student = situations.etudiant || false;
    this.userProfile.is_retired = situations.retraite || false;
    this.userProfile.is_unemployed = situations.chomeur || false;
    this.userProfile.is_disabled = situations.handicap || false;
    this.userProfile.is_employed = situations.aucune_situations || false;
  }

  getUserProfile() {
    return { ...this.userProfile };
  }

  // Données de session pour les événements
  getSessionContext() {
    return {
      device_type: getDeviceType(),
      viewport_width: window.innerWidth,
      viewport_height: window.innerHeight,
      referrer: document.referrer || null,
      referrer_category: getReferrerCategory(),
      utm_source: this._getUrlParam('utm_source'),
      utm_campaign: this._getUrlParam('utm_campaign'),
      utm_medium: this._getUrlParam('utm_medium'),
      utm_content: this._getUrlParam('utm_content')
    };
  }

  _getUrlParam(param) {
    try {
      // D'abord vérifier sessionStorage (UTM persistés au démarrage)
      const stored = sessionStorage.getItem(`sim_${param}`);
      if (stored) return stored;

      // Sinon vérifier l'URL actuelle
      const urlParams = new URLSearchParams(window.location.search);
      return urlParams.get(param) || null;
    } catch {
      return null;
    }
  }

  // Reset pour nouvelle session
  reset() {
    this.sessionId = this._generateSessionId();
    this.sessionStart = Date.now();
    this.currentStep = null;
    this.stepStartTime = null;
    this.visitedSteps.clear();
    this.completedSteps = [];
    this.stepErrors = {};
    this.validationAttempts = {};
    this.lastFieldFocused = null;
    this.fieldFocusEmitted = false;
    this.fieldsModified.clear();
    this.conditionalsShown = [];
    this.userProfile = {};

    try {
      sessionStorage.setItem('sim_session_id', this.sessionId);
      this._refreshExpiry();
    } catch (e) {
      // Ignore
    }
  }
}

export default SessionManager;
